import { get } from './client'
import { formatDateKeyLocal, getTodaySoFarRange } from '@/utils/timeRange'

// ==================== 统计数据类型 ====================

export interface DashboardStats {
  quota: number // 消耗额度
  token_count: number // Token 数量
  rpm: number // 每分钟请求数
  tpm: number // 每分钟 Token 数
}

export interface DailyData {
  date: string // 格式: YYYY-MM-DD
  quota: number // 消耗额度
  token_count: number // Token 数量
  request_count: number // 请求次数
  model_name?: string // 模型名（按模型聚合时使用）
  count?: number // 原始调用次数
  created_at?: number // 原始时间戳
  username?: string
  user_id?: number
  token_id?: number
  token_name?: string
}

export interface ModelDistribution {
  model_name: string
  quota: number
  percentage: number
}

export interface ChartDataPoint {
  date: string
  [key: string]: string | number // 动态模型名称
}

// ==================== API 参数类型 ====================

export interface StatsParams {
  type?: number // 日志类型
  start_timestamp?: number
  end_timestamp?: number
  token_name?: string
  token_id?: number
  model_name?: string
  group?: string
}

export interface DataQueryParams {
  start_timestamp?: number
  end_timestamp?: number
  username?: string // 管理员专用
  token_id?: number
}

// ==================== API 函数 ====================

/**
 * 获取用户统计摘要（今日/总计数据）
 * GET /api/log/self/stat
 *
 * 实际返回: { quota, rpm, tpm }（`token_count` 字段为历史类型残留，以服务端为准）
 */
export async function getUserStatsApi(params?: StatsParams): Promise<DashboardStats> {
  return get<DashboardStats>('/api/log/self/stat', params)
}

/**
 * 获取管理员统计摘要
 * GET /api/log/stat
 */
export async function getAdminStatsApi(params?: StatsParams): Promise<DashboardStats> {
  return get<DashboardStats>('/api/log/stat', params)
}

/**
 * 获取个人用量数据（按日期统计）
 * GET /api/data/self
 *
 * 用于：消耗趋势图、请求次数趋势图
 * 后端 `QuotaData`：count=请求次数、token_used=Token 用量、quota=消耗额度
 */
export async function getSelfDataApi(params?: DataQueryParams): Promise<DailyData[]> {
  const data = await get<
    Array<{
      count: number
      model_name: string
      quota: number
      created_at: number
      token_used?: number
      username?: string
      user_id?: number
      token_id?: number
      token_name?: string
    }>
  >('/api/data/self', params)

  return data.map((item) => ({
    date: formatDateKeyLocal(item.created_at),
    quota: item.quota || 0,
    token_count: item.token_used ?? 0,
    request_count: item.count || 0,
    model_name: item.model_name,
    count: item.count,
    created_at: item.created_at,
    username: item.username,
    user_id: item.user_id,
    token_id: item.token_id,
    token_name: item.token_name,
  }))
}

/**
 * 获取所有用户用量数据（管理员）
 * GET /api/data
 */
export async function getAllDataApi(params?: DataQueryParams): Promise<DailyData[]> {
  const data = await get<
    Array<{
      count: number
      model_name: string
      quota: number
      created_at: number
      token_used?: number
      username?: string
      user_id?: number
      token_id?: number
      token_name?: string
    }>
  >('/api/data', params)

  return data.map((item) => ({
    date: formatDateKeyLocal(item.created_at),
    quota: item.quota || 0,
    token_count: item.token_used ?? 0,
    request_count: item.count || 0,
    model_name: item.model_name,
    count: item.count,
    created_at: item.created_at,
    username: item.username,
    user_id: item.user_id,
    token_id: item.token_id,
    token_name: item.token_name,
  }))
}

// 系统状态接口统一由 ./status 提供，避免重复定义
export { getSystemStatusApi, getUptimeStatusApi } from './status'

// ==================== 工具函数 ====================

/**
 * 获取默认时间范围（最近7天）
 */
export function getDefaultTimeRange(): { start: number; end: number } {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - 7)

  return {
    start: Math.floor(start.getTime() / 1000),
    end: Math.floor(end.getTime() / 1000),
  }
}

/**
 * 获取「今日 0 点 ～ 当前」时间范围（顶栏「今日*」统计用，非筛选区「今天整」）
 */
export function getTodayTimeRange(): { start: number; end: number } {
  return getTodaySoFarRange()
}

/**
 * 处理图表数据（按模型分组）
 */
export function processChartData(
  data: Array<{
    created_at: number
    model_name: string
    quota: number
    count: number
  }>
): {
  dates: string[]
  byModel: Record<string, number[]>
  totalByModel: Record<string, number>
} {
  const dates: string[] = []
  const byModel: Record<string, number[]> = {}
  const totalByModel: Record<string, number> = {}

  // 按日期分组
  const groupedByDate = new Map<string, Map<string, { quota: number; count: number }>>()

  data.forEach((item) => {
    const date = formatDateKeyLocal(item.created_at)
    const model = item.model_name || '未知模型'

    if (!groupedByDate.has(date)) {
      groupedByDate.set(date, new Map())
    }

    const dateMap = groupedByDate.get(date)!
    const current = dateMap.get(model) || { quota: 0, count: 0 }
    dateMap.set(model, {
      quota: current.quota + (item.quota || 0),
      count: current.count + (item.count || 0),
    })

    // 累计总数
    totalByModel[model] = (totalByModel[model] || 0) + (item.quota || 0)
  })

  // 转换为数组格式
  const allModels = Object.keys(totalByModel)

  groupedByDate.forEach((dateMap, date) => {
    dates.push(date)
    allModels.forEach((model) => {
      if (!byModel[model]) {
        byModel[model] = []
      }
      const item = dateMap.get(model)
      byModel[model].push(item ? item.quota : 0)
    })
  })

  return { dates, byModel, totalByModel }
}
